import { Nullable } from 'src/core/common/domain/types/types';
import { Priority, PriorityPrimitives } from './priority';
import { NfcToolsConfiguration, NfcToolsConfigurationPrimitives } from './nfc-tools-configuration';

export type MasterDataConfigurationData = {
  display_service_address_in_task: boolean;
  display_postal_address_in_immediate_task: boolean;
  display_postal_address_in_planned_task: boolean;
  display_ambit_in_immediate_task: boolean;
  display_ambit_in_planned_task: boolean;
  display_description_in_immediate_task: boolean;
  display_description_in_planned_task: boolean;
  display_task_type_in_create_task: boolean;
  max_attachments: number;
  max_attachment_size: number;
  max_request_size: number;
  can_create_tasks: boolean;
  create_task_work_types: Array<{
    id: string;
    name: string;
  }>;
  create_task_priorities: Array<PriorityPrimitives>;
  create_task_default_priority: Nullable<number>;
  observations_for_client_inline: boolean;
  // IANA name of the zone the tenant operates in. Absent against backends that do not send it yet,
  // in which case TenantTimezone falls back to its default.
  timezone?: Nullable<string>;
  presence_check?: Nullable<{
    enabled: boolean;
    preselected_ambit_id: Nullable<string>;
    delay_seconds: Nullable<number>;
    response_timeout_seconds: Nullable<number>;
  }>;
  // Absent in configurations stored by app versions that predate the NFC tools, which grant none.
  nfc_tools?: NfcToolsConfigurationPrimitives;
};

export interface UserPresenceConfiguration {
  enabled: boolean;
  preselected_ambit_id: Nullable<string>;
  delay_seconds: Nullable<number>;
  response_timeout_seconds: Nullable<number>;
}

export const DEFAULT_USER_PRESENCE_CONFIGURATION = {
  delay_seconds: 10,
  response_timeout_seconds: 10,
};

export class MasterDataConfiguration {
  constructor(public readonly value: MasterDataConfigurationData) {}

  isDisplayServiceAddressInTask(): boolean {
    return this.value.display_service_address_in_task;
  }

  isDisplayPostalAddressInImmediateTask(): boolean {
    return this.value.display_postal_address_in_immediate_task;
  }

  isDisplayPostalAddressInPlannedTask(): boolean {
    return this.value.display_postal_address_in_planned_task;
  }

  isDisplayAmbitInImmediateTask(): boolean {
    return this.value.display_ambit_in_immediate_task;
  }

  isDisplayAmbitInPlannedTask(): boolean {
    return this.value.display_ambit_in_planned_task;
  }

  isDisplayDescriptionInImmediateTask(): boolean {
    return this.value.display_description_in_immediate_task;
  }

  isDisplayDescriptionInPlannedTask(): boolean {
    return this.value.display_description_in_planned_task;
  }

  isDisplayTaskTypeInCreateTask(): boolean {
    return this.value.display_task_type_in_create_task;
  }

  isDisplayInlineObservationsForClient(): boolean {
    return this.value.observations_for_client_inline;
  }

  getMaxAttachments(): number {
    return this.value.max_attachments;
  }

  getMaxAttachmentSize(): number {
    return this.value.max_attachment_size;
  }

  getMaxRequestSize(): number {
    return this.value.max_request_size;
  }

  getCreateTaskWorkTypes(): Array<{ id: string; name: string }> {
    return this.value.create_task_work_types;
  }

  getCreateTaskPriorities(): Array<Priority> {
    const priorities = this.value.create_task_priorities.map((priority) => Priority.fromPrimitives(priority));
    return priorities;
  }

  getCreateTaskDefaultPriority(): Nullable<number> {
    return this.value.create_task_default_priority;
  }

  canCreateTasks(): boolean {
    return this.value.can_create_tasks;
  }

  getTimezone(): Nullable<string> {
    return this.value?.timezone ?? null;
  }

  getPresenceConfiguration(): UserPresenceConfiguration {
    const enabled = this.value?.presence_check?.enabled ?? false;
    if (!this.value || !enabled) {
      return {
        enabled: false,
        preselected_ambit_id: this.value?.presence_check?.preselected_ambit_id ?? null,
        delay_seconds: null,
        response_timeout_seconds: null,
      };
    }

    return {
      enabled,
      preselected_ambit_id: this.value.presence_check?.preselected_ambit_id,
      delay_seconds: this.value.presence_check?.delay_seconds ?? DEFAULT_USER_PRESENCE_CONFIGURATION.delay_seconds,
      response_timeout_seconds:
        this.value.presence_check?.response_timeout_seconds ??
        DEFAULT_USER_PRESENCE_CONFIGURATION.response_timeout_seconds,
    };
  }

  getNfcTools(): NfcToolsConfiguration {
    return NfcToolsConfiguration.fromPrimitives(this.value?.nfc_tools);
  }
}
